/**
 * SavePresetModal Component
 *
 * Modal for saving the current layer configuration as a preset.
 * Captures layer enabled states, opacities, and order.
 */

import React, { useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSave, faGlobe, faUser, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useLayerDrawer } from '../LayerDrawerContext';
import { layerPresetService } from '../../../services/layerPresetService';
import { LayerPreset, PresetData } from '../../../types/layerPreset';
import { buildPresetData, buildPermalinkUrl } from '../../shareMap/shareMapState';
import './SavePresetModal.scss';

interface SavePresetModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after a successful save with the new saved map and its permalink
   * (already copied to the clipboard), so the parent can show the confirmation.
   */
  onSaved?: (result?: { preset: LayerPreset; url: string }) => void;
  isAdmin?: boolean;
}

export const SavePresetModal: React.FC<SavePresetModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  isAdmin = false,
}) => {
  const { layers, liveLayers, registry, map } = useLayerDrawer();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute current layer state for preview. Shared with the share-map-link
  // capture path (#557) so both serialize layer state identically.
  const presetData = useMemo((): PresetData => {
    return buildPresetData(layers, liveLayers, registry, map);
  }, [layers, liveLayers, registry, map]);

  // Count enabled layers for preview
  const enabledCount = useMemo(() => {
    const dbLayers = layers.filter(l => l.enabled && l.category !== 'backgrounds').length;
    const live = liveLayers.filter(l => l.enabled).length;
    const bg = layers.some(l => l.enabled && l.category === 'backgrounds') ? 1 : 0;
    return { overlays: dbLayers, live, background: bg, total: dbLayers + live + bg };
  }, [layers, liveLayers]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      setError('Please enter a name for the preset');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await layerPresetService.createPreset({
        name: name.trim(),
        description: description.trim() || undefined,
        is_global: isGlobal,
        preset_data: presetData,
      });

      // A saved map is shareable by its permalink — copy it to the clipboard
      // so Save and Share end identically.
      const url = buildPermalinkUrl(window.location.origin, created.id);
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Clipboard may be blocked; the confirmation modal still shows the link.
      }

      // Reset form
      setName('');
      setDescription('');
      setIsGlobal(false);

      // Hand the new saved map + permalink to the parent for confirmation.
      onSaved?.({ preset: created, url });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preset');
    } finally {
      setSaving(false);
    }
  }, [name, description, isGlobal, presetData, onSaved, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  }, [onClose, handleSave]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="save-preset-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="save-preset-modal" onClick={e => e.stopPropagation()}>
        <div className="save-preset-modal-header">
          <h3>Save Layer Preset</h3>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="save-preset-modal-body">
          {error && (
            <div className="save-preset-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="preset-name">Name *</label>
            <input
              id="preset-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Morning Briefing"
              maxLength={255}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="preset-description">Description (optional)</label>
            <textarea
              id="preset-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe what this preset is for..."
              rows={3}
            />
          </div>

          {isAdmin && (
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={e => setIsGlobal(e.target.checked)}
                />
                <FontAwesomeIcon icon={faGlobe} />
                <span>Make this a global preset (visible to all users)</span>
              </label>
            </div>
          )}

          <div className="preset-preview">
            <div className="preview-header">
              <FontAwesomeIcon icon={faLayerGroup} />
              <span>Layers to save</span>
            </div>
            <div className="preview-stats">
              <div className="stat">
                <span className="stat-value">{enabledCount.overlays}</span>
                <span className="stat-label">Overlays</span>
              </div>
              <div className="stat">
                <span className="stat-value">{enabledCount.live}</span>
                <span className="stat-label">Live Data</span>
              </div>
              <div className="stat">
                <span className="stat-value">{enabledCount.background}</span>
                <span className="stat-label">Background</span>
              </div>
            </div>
            {enabledCount.total === 0 && (
              <div className="preview-empty">
                No layers are currently active. Enable some layers before saving.
              </div>
            )}
          </div>

          <div className="preset-type-indicator">
            <FontAwesomeIcon icon={isGlobal ? faGlobe : faUser} />
            <span>
              {isGlobal
                ? 'This preset will be visible to all users'
                : 'This preset will be private to you'}
            </span>
          </div>
        </div>

        <div className="save-preset-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !name.trim() || enabledCount.total === 0}
          >
            <FontAwesomeIcon icon={faSave} />
            <span>{saving ? 'Saving...' : 'Save Preset'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
